/** グルーピングに必要な最小の形。一覧画面は Media の全項目を持たないことがある */
export interface HasTakenAt {
  /** ISO 8601。EXIF の撮影日時、無ければアップロード日時 */
  takenAt: string;
}

/** タイムラインの1ブロック(1ヶ月分) */
export interface MonthGroup<T extends HasTakenAt> {
  /** `YYYY-MM` */
  month: string;
  /** 表示用ラベル(例: `2026年8月`) */
  label: string;
  /** takenAt の降順 */
  items: T[];
}

/** 表示・グルーピングの基準タイムゾーン */
export const DEFAULT_TIME_ZONE = "Asia/Tokyo";

/**
 * `takenAt` を指定タイムゾーンで解釈し、`YYYY-MM` を返す。
 *
 * UTCで判定すると、8/1 00:30 JST に撮った写真が7月に落ちてしまう。
 * タイムラインは撮影した人の感覚に合わせる必要があるため、必ずローカル時刻で判定する。
 */
export function monthKeyOf(isoTimestamp: string, timeZone = DEFAULT_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date(isoTimestamp));

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;

  if (year === undefined || month === undefined) {
    throw new Error(`日時を解釈できません: ${isoTimestamp}`);
  }
  return `${year}-${month}`;
}

/** `2026-08` → `2026年8月` */
export function monthLabelOf(monthKey: string): string {
  const [year, month] = monthKey.split("-");
  if (year === undefined || month === undefined) {
    throw new Error(`月キーの形式が不正です: ${monthKey}`);
  }
  return `${year}年${Number(month)}月`;
}

/** JST は夏時間が無いので固定の +9 時間で計算できる */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function monthKeyPartsOf(monthKey: string): { year: number; month: number } {
  const [year, month] = monthKey.split("-");
  if (year === undefined || month === undefined) {
    throw new Error(`月キーの形式が不正です: ${monthKey}`);
  }
  const y = Number(year);
  const m = Number(month);
  if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) {
    throw new Error(`月キーの形式が不正です: ${monthKey}`);
  }
  return { year: y, month: m };
}

/**
 * その月の最初の瞬間(= 1日 0時 JST)を返す。
 * monthEndOf と組で `これ <= taken_at < monthEndOf` が「その月だけ」になる。
 */
export function monthStartOf(monthKey: string): string {
  const { year, month } = monthKeyPartsOf(monthKey);
  return new Date(Date.UTC(year, month - 1, 1) - JST_OFFSET_MS).toISOString();
}

/**
 * その月より後の最初の瞬間(= 翌月1日 0時)を返す。
 * `taken_at < これ` で「その月以前」が取れる。
 */
export function monthEndOf(monthKey: string): string {
  const { year, month } = monthKeyPartsOf(monthKey);
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return new Date(Date.UTC(nextYear, nextMonth - 1, 1) - JST_OFFSET_MS).toISOString();
}

/**
 * メディアを月単位にグルーピングする(要件定義 3.2「自動整理」)。
 *
 * 写真・動画は区別せず同じタイムラインに混在させる。
 * 月・メディアともに新しい順に並べる。
 */
export function groupMediaByMonth<T extends HasTakenAt>(
  media: readonly T[],
  timeZone = DEFAULT_TIME_ZONE,
): MonthGroup<T>[] {
  const groups = new Map<string, T[]>();

  for (const item of media) {
    const key = monthKeyOf(item.takenAt, timeZone);
    const bucket = groups.get(key);
    if (bucket === undefined) {
      groups.set(key, [item]);
    } else {
      bucket.push(item);
    }
  }

  return [...groups.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([month, items]) => ({
      month,
      label: monthLabelOf(month),
      items: [...items].sort((a, b) => b.takenAt.localeCompare(a.takenAt)),
    }));
}
